import axios from 'axios'
import authService from './authService'

// API Configuration
// In development, use relative URL (proxy handles it)
// In production, use the full URL from environment variable
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? '/api' 
  : (process.env.VUE_APP_SERVER_URL || '/not-worked') + '/api'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000 // 10 second timeout
})

// Track if we're currently refreshing a token to prevent multiple refresh attempts
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  
  failedQueue = []
}

// Request interceptor for adding auth headers
apiClient.interceptors.request.use(
  async (config) => {
    // Skip token refresh check for the refresh endpoint itself to prevent infinite loop
    if (config.skipAuthRefresh) {
      const token = authService.getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    }

    // Check if token is about to expire and refresh if needed
    if (authService.isAuthenticated() && authService.isTokenExpired()) {
      if (!isRefreshing) {
        isRefreshing = true

        try {
          await authService.refreshToken()
          isRefreshing = false
          processQueue(null, authService.getToken())
        } catch (error) {
          isRefreshing = false
          processQueue(error, null)
          // If refresh fails, logout and reload page
          authService.logout()
          console.error('Token refresh failed in request interceptor, logging out and reloading')
          // Force a full page reload to reset all state
          window.location.href = '/'
          return Promise.reject(error)
        }
      } else {
        // Wait for the current refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          config.headers.Authorization = `Bearer ${token}`
          return config
        }).catch(err => {
          return Promise.reject(err)
        })
      }
    }

    // Add authentication token if available
    const token = authService.getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add any other global headers here
    config.headers['X-Client-Version'] = '1.0.0'
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for handling common errors
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Handle common error scenarios
    if (error.response) {
      // Server responded with error status
      switch (error.response.status) {
        case 401:
          // Don't retry if this was the refresh endpoint itself - just logout
          if (originalRequest.skipAuthRefresh) {
            authService.logout()
            console.error('Token refresh failed - token is invalid or expired, logging out')
            window.location.href = '/'
            return Promise.reject(error)
          }

          // Unauthorized - try to refresh token first
          if (!originalRequest._retry && authService.isAuthenticated()) {
            originalRequest._retry = true

            try {
              await authService.refreshToken()
              // Retry the original request with new token
              originalRequest.headers.Authorization = `Bearer ${authService.getToken()}`
              return apiClient(originalRequest)
            } catch (refreshError) {
              // If refresh fails, logout and reload page
              authService.logout()
              console.error('Token refresh failed, logging out and reloading')
              // Force a full page reload to reset all state
              window.location.href = '/'
              return Promise.reject(refreshError)
            }
          }

          // Already tried refresh or not authenticated
          // Show login modal if there's no token
          if (!authService.isAuthenticated()) {
            console.error('Unauthorized access - not authenticated, showing login modal')
            window.dispatchEvent(new CustomEvent('show-auth-modal', {
              detail: { reason: 'unauthorized' }
            }))
          }
          return Promise.reject(error)
        case 403:
          // Forbidden
          console.error('Access forbidden')
          break
        case 404:
          // Not found
          console.error('Resource not found')
          break
        case 500:
          // Server error
          console.error('Server error')
          break
        default:
          console.error(`HTTP Error: ${error.response.status}`)
      }
    } else if (error.request) {
      // Network error
      console.error('Network error - no response received')
    } else {
      // Other error
      console.error('Request error:', error.message)
    }
    
    return Promise.reject(error)
  }
)

export default apiClient 